import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArticlePage = () => {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    axios
      .get(`${API}/blog/posts/${slug}`)
      .then((res) => {
        setPost(res.data);
        const title = lang === "en" && res.data.title_en ? res.data.title_en : res.data.title_fr;
        document.title = `Orbit — ${title}`;
      })
      .catch(() => setNotFound(true));
  }, [slug, lang]);

  if (notFound) {
    return (
      <main data-testid="article-not-found" className="pt-[76px]">
        <div className="mx-auto max-w-3xl px-5 py-32 text-center">
          <p className="text-slate-500">404</p>
          <Link to="/ressources" className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-blue-600">
            <ArrowLeft className="h-4 w-4" /> {t.blog.back}
          </Link>
        </div>
      </main>
    );
  }

  if (!post) {
    return <main className="min-h-screen pt-[76px]" data-testid="article-loading" />;
  }

  const title = (lang === "en" && post.title_en) || post.title_fr;
  const content = ((lang === "en" && post.content_en) || post.content_fr || "").split(/\n\n+/);

  return (
    <main data-testid="article-page" className="pt-[76px]">
      <article className="py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Reveal>
            <Link
              to="/ressources"
              data-testid="article-back-link"
              className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 transition-colors hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4" /> {t.blog.back}
            </Link>
            <p className="mt-10 text-[11px] font-semibold uppercase tracking-widest text-emerald-500">
              {t.blog.publishedOn}{" "}
              {new Date(post.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
            <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl text-balance">
              {title}
            </h1>
          </Reveal>
          {post.cover_image && (
            <Reveal delay={0.1}>
              <img
                src={post.cover_image}
                alt={title}
                className="mt-10 aspect-[2/1] w-full rounded-2xl object-cover"
              />
            </Reveal>
          )}
          <Reveal delay={0.15}>
            <div className="mt-10 space-y-6" data-testid="article-content">
              {content.map((paragraph, i) => (
                <p key={i} className="text-base leading-relaxed text-slate-700 sm:text-lg">
                  {paragraph}
                </p>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="relative mt-14 overflow-hidden rounded-2xl bg-[#0A0F1D] p-9">
              <div className="pointer-events-none absolute inset-0 bg-grid-navy" />
              <div className="relative">
                <h2 className="font-display text-xl font-bold text-white">{t.blog.ctaTitle}</h2>
                <p className="mt-2 max-w-md text-sm text-slate-400">{t.blog.ctaText}</p>
                <Link
                  to="/demo"
                  data-testid="article-cta-button"
                  className="group mt-5 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-500"
                >
                  {t.blog.ctaButton}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </article>
    </main>
  );
};

export default ArticlePage;
